import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

/* ── Helpers ── */
const NAV_ITEMS = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Portfolio', path: '/portfolio' },
  { name: 'Connect', path: '/connect' }
];

function getLinkClass(isActive, textColor) {
  if (isActive) return 'text-[#B8960C]';
  return `${textColor} hover:text-[#B8960C]`;
}

/* ── Sub-components ── */
const NavLogo = ({ onClick, logoColor, logoSubColor }) => (
  <button data-testid="nav-logo-button" onClick={onClick} className="cursor-pointer group">
    <div className="text-center">
      <div className={`font-heading text-xl md:text-2xl font-light uppercase ${logoColor}`} style={{ letterSpacing: '0.25em' }}>
        Hinton
      </div>
      <div className="w-full h-px bg-[#B8960C] my-1 opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className={`font-heading text-[10px] font-light uppercase ${logoSubColor}`} style={{ letterSpacing: '0.35em' }}>
        Enterprises
      </div>
    </div>
  </button>
);

const DesktopLinks = ({ items, currentPath, textColor, onNavigate }) => (
  <div className="hidden md:flex items-center space-x-10">
    {items.map((item) => (
      <button
        key={item.name}
        data-testid={`nav-link-${item.name.toLowerCase()}`}
        onClick={() => onNavigate(item.path)}
        className={`text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${getLinkClass(currentPath === item.path, textColor)}`}
      >
        {item.name}
      </button>
    ))}
  </div>
);

const MobileMenu = ({ items, currentPath, textColor, isHero, onNavigate }) => (
  <div data-testid="mobile-menu" className={`md:hidden py-8 border-t ${isHero ? 'border-white/10' : 'border-black/5'}`}>
    {items.map((item) => (
      <button
        key={item.name}
        data-testid={`mobile-nav-link-${item.name.toLowerCase()}`}
        onClick={() => onNavigate(item.path)}
        className={`block w-full text-left py-4 text-xs font-medium uppercase tracking-[0.2em] transition-colors ${getLinkClass(currentPath === item.path, textColor)}`}
      >
        {item.name}
      </button>
    ))}
  </div>
);

/* ── Main component ── */
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHero, setIsHero] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      setIsHero(window.scrollY < window.innerHeight - 100 && location.pathname === '/');
    };
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const handleNavigate = useCallback((path) => {
    if (path === '/' && location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(path);
    }
    setMobileMenuOpen(false);
  }, [navigate, location.pathname]);

  const textColor = isHero ? 'text-white/70' : 'text-[#1A1A1A]/60';
  const logoColor = isHero ? 'text-white' : 'text-[#1A1A1A]';
  const logoSubColor = isHero ? 'text-white/80' : 'text-[#1A1A1A]/70';

  let navBg = 'bg-transparent';
  if (scrolled) {
    navBg = isHero
      ? 'backdrop-blur-xl bg-black/40 border-b border-white/10'
      : 'backdrop-blur-xl bg-white/90 border-b border-black/5 shadow-sm';
  }

  return (
    <nav data-testid="main-navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex justify-between items-center h-20 md:h-24">
          <NavLogo onClick={() => handleNavigate('/')} logoColor={logoColor} logoSubColor={logoSubColor} />
          <DesktopLinks items={NAV_ITEMS} currentPath={location.pathname} textColor={textColor} onNavigate={handleNavigate} />
          <button
            data-testid="mobile-menu-toggle"
            className={`md:hidden ${textColor} hover:text-[#B8960C] transition-colors`}
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} strokeWidth={1} /> : <Menu size={22} strokeWidth={1} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <MobileMenu items={NAV_ITEMS} currentPath={location.pathname} textColor={textColor} isHero={isHero} onNavigate={handleNavigate} />
        )}
      </div>
    </nav>
  );
};

export default Navigation;
